import Papa from 'papaparse';
import type { BulkMappingRow } from '../types/bulkOnboarding.js';
import { recomputeRowFlags } from './onboardingMapping.js';

// Pure CSV round-trip for the bulk-onboarding mapping (no DOM). For ~500 clubs
// an AM prepares/corrects the mapping in a spreadsheet rather than hand-picking
// 500 dropdowns. Export → edit → re-import. papaparse handles quoting.

const COLUMNS = [
  'accountId',
  'accountName',
  'profileName',
  'pageId',
  'pageName',
  'adAccountId',
  'adAccountName',
] as const;

export function mappingRowsToCsv(rows: BulkMappingRow[]): string {
  const data = rows.map((r) => [
    r.accountId,
    r.accountName,
    r.profileName,
    r.pageId ?? '',
    r.pageName ?? '',
    r.adAccountId ?? '',
    r.adAccountName ?? '',
  ]);
  return Papa.unparse({ fields: [...COLUMNS], data });
}

export type ParsedMappingCsvRow = {
  accountId: string;
  accountName: string;
  profileName: string;
  pageId: string;
  pageName: string;
  adAccountId: string;
  adAccountName: string;
};

export type CsvParseResult = {
  rows: ParsedMappingCsvRow[];
  errors: string[]; // human-readable, per-problem
};

function cell(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

// Parse raw CSV text into typed rows. Header-driven (column order-independent);
// reports a missing-header error and skips rows with no accountId.
export function parseMappingCsv(text: string): CsvParseResult {
  const parsed = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    skipEmptyLines: true,
  });
  const errors: string[] = [];
  const headerFields = parsed.meta.fields ?? [];
  if (!headerFields.includes('accountId')) {
    errors.push('CSV is missing the required "accountId" column.');
    return { rows: [], errors };
  }

  const rows: ParsedMappingCsvRow[] = [];
  (parsed.data ?? []).forEach((raw, i) => {
    const accountId = cell(raw.accountId);
    if (!accountId) {
      errors.push(`Row ${i + 2}: missing accountId — skipped.`);
      return;
    }
    rows.push({
      accountId,
      accountName: cell(raw.accountName),
      profileName: cell(raw.profileName),
      pageId: cell(raw.pageId),
      pageName: cell(raw.pageName),
      adAccountId: cell(raw.adAccountId),
      adAccountName: cell(raw.adAccountName),
    });
  });
  return { rows, errors };
}

// Merge parsed CSV rows back onto the known un-onboarded accounts. Unknown
// accountIds are reported (not silently kept); rows for known accounts are
// rebuilt with recomputed ready/needsReview flags. profileName falls back to
// the account name when blank.
export function applyCsvToMapping(
  parsed: ParsedMappingCsvRow[],
  knownAccounts: Map<string, string>, // accountId → accountName
): { rows: BulkMappingRow[]; errors: string[] } {
  const errors: string[] = [];
  const rows: BulkMappingRow[] = [];
  const seen = new Set<string>();
  for (const p of parsed) {
    const accountName = knownAccounts.get(p.accountId);
    if (accountName === undefined) {
      errors.push(`Unknown or already-onboarded account "${p.accountId}" — ignored.`);
      continue;
    }
    if (seen.has(p.accountId)) {
      errors.push(`Duplicate row for account "${p.accountId}" — first kept.`);
      continue;
    }
    seen.add(p.accountId);
    rows.push(
      recomputeRowFlags({
        accountId: p.accountId,
        accountName,
        profileName: p.profileName || accountName,
        pageId: p.pageId || null,
        pageName: p.pageName || null,
        pageUsername: null, // not a CSV column — handle is display-only metadata
        pageConfidence: 0,
        adAccountId: p.adAccountId || null,
        adAccountName: p.adAccountName || null,
        adAccountConfidence: 0,
        adAccountSource: p.adAccountId ? 'manual' : null,
        needsReview: false,
        ready: false,
        confirmed: false, // re-imported rows must be re-confirmed before running
      }),
    );
  }
  return { rows, errors };
}
